import { useCallback, useEffect, useRef, useState } from "react";

/**
 * `ODF-1` (ORDRE-DECAISSE-FIX; the designer's triage, « the 2-second moment after « Fonds versés » »).
 *
 * The funds door answers once the two transfers' references are recorded; the file moves to `decaisse` a moment later, by the
 * event `payments.FundsDisbursed` and its consumer (04 §4.1: the outbox is the only event transport, drained every 500 ms
 * and on `NOTIFY`). Until then the page must not read « Conditions 6/6 » with its form. The moment has four states:
 *
 *  - `idle`: nothing is happening;
 *  - `saving`: the door has not answered; the primary reads « Enregistrement… » and is inert;
 *  - `waiting`: the door answered, the file is not yet `decaisse`; the order card is one information line;
 *  - `late`: 5 s after the answer the file has not changed stage; the page says so and offers « Recharger ».
 *
 * While `waiting` the page re-reads the file itself, by the caller's own read, once at +1 s and once at +3 s after the answer.
 * Everything stops the moment the stage is `decaisse`, when another file is opened and when the screen goes away; a refused
 * door goes back to `idle` and hands its refusal to the caller. The timers are the browser's, never a clock of the product.
 */
export type FundsMoment = "idle" | "saving" | "waiting" | "late";

/** The re-reads after the answer, in milliseconds, and the instant the page gives up waiting and says so. */
export const FUNDS_REREADS_MS: readonly number[] = [1000, 3000];
export const FUNDS_LATE_MS = 5000;

export function useFundsMoment(args: {
  readonly fileId: string | null;
  readonly stage: string | null;
  readonly reread: (fileId: string) => Promise<void>;
}): {
  readonly moment: FundsMoment;
  /** `send` is the funds door; `settled` the immediate re-read once it answered. Rejects with the door's refusal. */
  readonly run: (send: () => Promise<void>, settled: () => Promise<void>) => Promise<void>;
} {
  const [moment, setMoment] = useState<FundsMoment>("idle");
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const live = useRef(args);
  live.current = args;
  const decaisse = args.stage === "decaisse";

  const stop = useCallback((): void => {
    for (const timer of timers.current) clearTimeout(timer);
    timers.current = [];
  }, []);

  // The stage reached `decaisse`, or another file is open: the moment is over.
  useEffect(() => {
    stop();
    setMoment("idle");
  }, [args.fileId, decaisse, stop]);

  // The screen goes away: no timer outlives it.
  useEffect(() => stop, [stop]);

  const run = useCallback(
    async (send: () => Promise<void>, settled: () => Promise<void>): Promise<void> => {
      const openedFor = live.current.fileId;
      stop();
      setMoment("saving");
      try {
        await send();
      } catch (failure) {
        setMoment("idle");
        throw failure;
      }
      if (openedFor === null || live.current.fileId !== openedFor) return;
      setMoment("waiting");
      // The clock starts at the answer; the immediate re-read below is the page's own, as before.
      for (const after of FUNDS_REREADS_MS) {
        timers.current.push(
          setTimeout(() => {
            void live.current.reread(openedFor).catch(() => undefined);
          }, after),
        );
      }
      timers.current.push(
        setTimeout(() => {
          if (live.current.fileId === openedFor && live.current.stage !== "decaisse") setMoment("late");
        }, FUNDS_LATE_MS),
      );
      await settled();
    },
    [stop],
  );

  return { moment, run };
}
